import type { StripeElementLocale } from '@stripe/stripe-js';
import { logWarn } from '@/sdkConsole';
import { resolveCatalogLocale } from './resolve';
import { setCatalog } from './t';
import { catalogUrls } from './manifest';
import type { Catalog } from './types';

// Statics host for the per-locale catalog JSON, baked in at build time (the
// catalogs deploy with min.js — see docs/plans/2026-08-26-widget-translations.md).
// `data-static` on the script tag is an OPTIONAL override for local dev, mirroring
// data-api/data-portal. Never derive the host from the script's own URL: page
// optimizers re-execute min.js from a blob: URL (docs/architecture/widget-embed.md).
const DEFAULT_STATIC_BASE = 'https://sm.ezoic.com';

let staticBase: string | null = null;

function getStaticBase(): string {
  if (staticBase !== null) return staticBase;
  const override = document
    .querySelector<HTMLScriptElement>('script[data-static]')
    ?.getAttribute('data-static');
  staticBase = override ?? DEFAULT_STATIC_BASE;
  return staticBase;
}

let stripeLocale = 'auto';
let appliedLocale: string | null = null;
// Retain direction for hosts created after their config response.
let resolvedLocale = 'en';

// Locales whose chrome renders right-to-left. Direction is a function of the
// resolved catalog locale (what actually rendered), not the raw setting, so
// `auto` + an Arabic browser flips too. Keyed off the same value
// applyVisitorLanguage computes.
const RTL_LOCALES = new Set(['ar', 'he']);

// applyDirectionTo pins the host's direction to the resolved catalog locale:
// dir="rtl" for RTL locales, dir="ltr" otherwise. Setting ltr explicitly (not
// removing the attribute) isolates the widget from the publisher page — `all:
// initial` on :host does not reset `direction`, so without this an RTL
// publisher page (<html dir="rtl">) would mirror English chrome. Both modal and
// inline hosts use the domain's visitorLanguage value. dir inherits
// into the shadow tree, flipping flex/grid and default text alignment; the
// physical→logical CSS in styles.css handles the rest.
function applyDirectionTo(host: HTMLElement, locale: string): void {
  host.setAttribute('dir', RTL_LOCALES.has(locale) ? 'rtl' : 'ltr');
}

// applyHostDirection applies the current resolved locale's direction to the
// shadow host. The sdk calls this when it creates a modal or inline host,
// covering first mount; applyVisitorLanguage calls it on every config response,
// covering later locale changes.
export function applyHostDirection(host: HTMLElement): void {
  applyDirectionTo(host, resolvedLocale);
}

// getStripeLocale is the locale to pass to stripe.elements(): 'auto' (Stripe's
// own browser detection) while the domain is in auto mode, or the publisher's
// explicit choice so the Stripe fields agree with our chrome. The cast is safe:
// the explicit values are validated against the Stripe Elements locale set
// (pkg/domainsetting/locales.json) before they can be stored.
export function getStripeLocale(): StripeElementLocale {
  return stripeLocale as StripeElementLocale;
}

// applyVisitorLanguage applies the domain's visitorLanguage setting from any
// onsite config response (they all carry the same domain value, so last call
// wins harmlessly). 'auto' resolves from the browser; an explicit locale forces
// it. A resolved 'en' (or an unsupported/unshipped locale) needs no fetch — the
// in-bundle defaults are the English catalog. Catalog load failure leaves the
// widget in English by design: the paywall must never block on a translation.
export function applyVisitorLanguage(setting: string | undefined): void {
  const requested = setting && setting.trim() !== '' ? setting.trim() : 'auto';
  stripeLocale = requested === 'auto' ? 'auto' : requested;
  const locale = requested === 'auto' ? resolveCatalogLocale(navigator.language) : resolveCatalogLocale(requested);
  // Direction tracks the resolved locale on every call — including the 'en'
  // short-circuit and the already-applied early return — so a locale change
  // always re-pins dir (rtl for ar/he, ltr otherwise). If the host doesn't
  // exist yet, the SDK applies it via applyHostDirection when creating it.
  resolvedLocale = locale;
  for (const host of document.querySelectorAll<HTMLElement>('#ezoic-sm, [data-ezoic-sm-checkout]')) {
    applyDirectionTo(host, locale);
  }
  if (locale === 'en' || locale === appliedLocale) {
    return;
  }
  appliedLocale = locale;
  loadCatalog(locale);
}

async function loadCatalog(locale: string): Promise<void> {
  // The catalog path is baked into min.js at build time (content-hashed in
  // production, unhashed in dev). A missing entry means the build shipped no
  // catalog for this locale — stay English, same as a 404.
  const path = catalogUrls[locale];
  if (!path) {
    logWarn('i18n catalog not in build, staying in English', locale);
    appliedLocale = null;
    return;
  }
  try {
    const resp = await fetch(`${getStaticBase()}/${path}`);
    if (!resp.ok) {
      logWarn('i18n catalog unavailable, staying in English', locale, resp.status);
      appliedLocale = null;
      return;
    }
    const catalog = (await resp.json()) as Catalog;
    setCatalog(locale, catalog);
  } catch (err) {
    logWarn('i18n catalog fetch failed, staying in English', locale, err);
    // Clear the marker so the next config response retries — one transient
    // failure must not pin the page to English.
    appliedLocale = null;
  }
}

// Test hook: reset the module state between cases.
export function resetVisitorLanguageForTest(): void {
  stripeLocale = 'auto';
  appliedLocale = null;
  resolvedLocale = 'en';
  setCatalog('en', {});
}
